import React, {useState, useEffect, useCallback} from 'react';
import update from 'immutability-helper';
import {NavLink as Link} from 'react-router-dom';
import PublishStatusDot from '../../../misc/PublishStatusDot.js';
import tagModel from '../../../../model/tag';
import TagSuggestions from '../../../misc/TagSuggestions.js';
import ImageResponsive from '../../../contentful/ImageResponsive.js';
import FileSize from '../../../misc/FileSize.js';
import {createTooltip, generateEditLinkWithAutoResolve} from '../../../misc/utils.js';
import {OverlayTrigger} from 'react-bootstrap';

const Media = (props) => {
  const {
    onSave,
    onChange,
    onArchive,
    onUnarchive,
    working,
    mediaItem,
    modified,
    error,
    tags,
    refreshTags,
    linkedEntries
  } = props;
  const [resolvedLinkedEntries, setResolvedLinkedEntries] = useState([]);
  const [resolveError, setResolveError] = useState('');

  const resolveLinkedEntries = async (items) => {
    try {
      const result = [];
      for (let i = 0; i < items.length; i++) {
        const entry = items[i];
        const resolvedEntry = await generateEditLinkWithAutoResolve(
          entry.sys.contentType.sys.id,
          entry.sys.id,
          entry.fields.displayname?.['nb-NO'] ?? entry.fields.name?.['nb-NO'] ?? 'Ukjent navn'
        );
        result.push(resolvedEntry);
      }

      setResolvedLinkedEntries(result);
    } catch (error) {
      console.error(error);
      setResolveError('Problem oppstod under henting av tilknyttede objekter');
    }
  };

  useEffect(() => {
    if (linkedEntries) {
      resolveLinkedEntries(linkedEntries);
    }
  }, [linkedEntries]);


  /* Handle submit */
  const handleSubmit = (e) => {
    e.preventDefault();
    onSave();
  };

  const handleAssetChange = (name, asset) => {
    const change = {
      mediaItem: {}
    };

    change.mediaItem[name] = {
      $set: asset
    };

    const newProps = update(props, change);
    onChange(newProps);
  };

  const handleFieldChange = (e) => {
    return handleAssetChange(e.target.name, e.target.value);
  };

  const onDelete = useCallback((tagIndex) => {
    const newData = {
      mediaItem: {}
    };

    const tags = props.mediaItem.tags;

    const updatedTags = tags.filter((_, i) => i !== tagIndex);

    newData.mediaItem.tags = {
      $set: updatedTags
    };

    const newProps = update(props, newData);
    onChange(newProps);
  }, [props.mediaItem.tags]);

  const onAddition = useCallback(async (newTag) => {
    const newData = {
      mediaItem: {}
    };

    const tagsArray = props.mediaItem.tags;

    if (tagsArray.some((tag) => tag.id === newTag.id)) {
      return;
    }

    // Check if we are dealing with a new tag, and if so send an update to Contentful to create the tag.
    if (!tags.some((tag) => tag.name === newTag.name)) {
      newTag = await tagModel.create(newTag);

      // Refresh tags so that the new item is added to suggestions from the API.
      refreshTags();
    }

    tagsArray.push(newTag);

    newData.mediaItem.tags = {
      $set: tagsArray
    };

    const newProps = update(props, newData);
    onChange(newProps);
  }, [props.mediaItem.tags]);

  const suggestions = tags.filter((tag) => !mediaItem.tags.some((mediaItemTag) => mediaItemTag.id === tag.id));
  const showSecondColumn = mediaItem.details.image || linkedEntries.length > 0 || mediaItem.contentType.startsWith('video/');

  return (
    <section className="mf-equipment-equipment">
      <ol className="breadcrumb mf-margin-top-20">
        <li><Link to="/">Forsiden</Link></li>
        <li><Link to="/media/">Mediautforsker</Link></li>
        <li className="active">{mediaItem.title}</li>
      </ol>

      <form onSubmit={handleSubmit}>
        <div className="page-header">
          <div className="row">
            <div className="col-xs-12 col-sm-8">
              <h1>
                {mediaItem.title} <PublishStatusDot entry={mediaItem} /><br/>
                <small>Mediaelement</small>
              </h1>
            </div>

            <div className="col-xs-12 col-sm-4 text-right mf-padding-top-10">
              {working &&
                <span>&nbsp;<i className="fa fa-spinner fa-pulse fa-fw"></i> Arbeider...</span>
              }
              {!mediaItem.archived &&
                <button
                  type="submit"
                  className="btn btn-default"
                  disabled={!modified || working}
                >
                  <span>Lagre endringer</span>
                </button>
              }
              &nbsp;
              {!mediaItem.archived && (
                <>
                  <button
                    type="button"
                    className="btn btn-danger"
                    disabled={working || linkedEntries.length > 0}
                    onClick={(e) => {
                      e.preventDefault();
                      onArchive();
                    }}
                  >
                    <span>Arkiver</span>
                  </button>
                  {linkedEntries.length > 0 && (
                    <>
                      &nbsp;
                      <OverlayTrigger placement="top" overlay={createTooltip('Kan ikke arkivere filer tilknyttet et objekt')}>
                        <span className="fa fa-exclamation-triangle text-warning" aria-hidden="true" />
                      </OverlayTrigger>
                    </>
                  )}
                </>
              )}
              &nbsp;
              {mediaItem.archived &&
                <button
                  type="button"
                  className="btn btn-danger"
                  disabled={working}
                  onClick={(e) => {
                    e.preventDefault();
                    onUnarchive();
                  }}
                >
                  <span>Gjenopprett</span>
                </button>
              }
            </div>
          </div>
        </div>

        {/* Display any error message: */}
        {error &&
          <div className="alert alert-danger">
            {error}
          </div>
        }

        <div className="row">
          <div className={`col-xs-12 ${showSecondColumn ? 'col-md-6' : 'col-md-12'}`}>
            <div className="col-xs-12 col-md-12">
              <div className="form-group">
                <label>Filinformasjon</label>
                <p>Opprettet: {mediaItem.createdAt.toLocaleString('nb')}</p>
                <p>Sist oppdatert: {mediaItem.updatedAt.toLocaleString('nb')}</p>
              </div>
            </div>
            <div className="col-xs-12 col-md-12">
              <div className="form-group">
                <label>URL</label>
                <p><a href={mediaItem.url}>{mediaItem.url}</a></p>
              </div>
            </div>
            <div className="col-xs-12 col-md-6">
              <div className="form-group">
                <label>Navn</label>
                <input type="text" className="form-control" name="title" disabled={working || mediaItem.archived} placeholder={mediaItem.title} defaultValue={mediaItem.title} onChange={handleFieldChange} />
              </div>
            </div>
            <div className="col-xs-12 col-md-6">
              <div className="form-group">
                <label>Beskrivelse</label>
                <input type="text" className="form-control" name="description" disabled={working || mediaItem.archived} placeholder={mediaItem.description} defaultValue={mediaItem.description} onChange={handleFieldChange} />
              </div>
            </div>
            <div className="col-xs-12 col-md-12">
              <div className="form-group">
                <label>Tags</label>
                <TagSuggestions
                  tags={mediaItem.tags}
                  suggestions={suggestions}
                  minQueryLength={1}
                  onDelete={onDelete}
                  onAddition={onAddition}
                  allowNew={true}
                  newTagText='Opprett ny tag:'
                  placeholderText='Begynn å skrive for eksisterende forslag eller opprette nye'
                  className="form-control"
                />
              </div>
            </div>
          </div>
          {showSecondColumn && (
            <div className="col-xs-12 col-md-6">
              {linkedEntries.length > 0 && (
                <div style={{border: '1px'}}>
                  <label>Tilknyttede objekter:</label>
                  {resolveError && (
                    <p>{resolveError}</p>
                  )}
                  {resolvedLinkedEntries.length > 0 && (
                    <ul>
                      {resolvedLinkedEntries.map((entry) => (
                        <li key={`entry-${entry.id}`}>
                          <a href={entry.url}>
                            {entry.name}
                          </a>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              )}
              {mediaItem.details.image && (
                <>
                  <div style={{border: '1px'}}>
                    <label>Bildedata:</label>
                    <p>Størrelse: {mediaItem.details.image.height}x{mediaItem.details.image.width}px - Filstørrelse: <FileSize bytes={mediaItem.details.size} /></p>
                  </div>
                  <ImageResponsive
                    className="img-rounded"
                    src={mediaItem.url}
                    width={mediaItem.details.image ? mediaItem.details.image.width : 300}
                    height={mediaItem.details.image ? mediaItem.details.image.height : 300}
                    fit="scale"
                  />
                </>
              )}
              {mediaItem.contentType.startsWith('video/') && (
                <>
                  <div style={{border: '1px'}}>
                    <label>Videodata:</label>
                    <p>Filstørrelse: <FileSize bytes={mediaItem.details.size} /></p>
                  </div>
                  <video style={{width: '100%'}} controls preload="none" src={mediaItem.url}></video>
                </>
              )}
            </div>
          )}
        </div>
      </form>
    </section>
  );
};

export default Media;
